import { useEffect, useMemo } from 'react';

import { Constants } from '@constants';
import { numbersOnly } from '@forms/helpers/normalisers';
import { getCountry } from '@iwIntlProvider/CountryProvider';
import { FormattedCurrency } from '@iwIntlProvider/FormattedCurrency';
import { IconEuro, IconPound } from '@iwoca/icons';
import { ErrorMessage, Input, Slider, Text } from '@iwoca/orion';
import _ from 'lodash';
import { FormattedMessage, useIntl } from 'react-intl';

import {
    selectedAmountGreaterThanMaxAvailableCash,
    selectedAmountLessThanMinDrawDown
} from './isSelectedAmountValid';

export const ChooseAmountInputWithSlider = ({
    label,
    description,
    selectedAmount,
    onChange,
    maxAmount
}: {
    label: React.ReactNode;
    description?: React.ReactNode;
    selectedAmount: number | null;
    onChange: (
        amount: number,
        event: React.ChangeEvent<HTMLInputElement>
    ) => void;
    maxAmount: number;
}) => {
    const intl = useIntl();

    const minAmount = Constants.MIN_DRAW_DOWN_AMOUNT;

    const debounceSetSelectedAmount = useMemo(
        () =>
            _.debounce(
                (value: string, event: React.ChangeEvent<HTMLInputElement>) => {
                    const numericValue = parseInt(numbersOnly(value), 10);
                    if (isNaN(numericValue)) {
                        onChange(0, event);
                    } else {
                        onChange(numericValue, event);
                    }
                },
                100
            ),
        [onChange]
    );

    useEffect(() => {
        return () => {
            debounceSetSelectedAmount.cancel();
        };
    }, [debounceSetSelectedAmount]);

    const selectedAmountOrMaxAmount = selectedAmount ?? maxAmount;

    return (
        <div className="w-full space-y-xl">
            <div className="space-y-m">
                <div>
                    {label}
                    {description ? (
                        <Text className="text-s text-structure-40">
                            {description}
                        </Text>
                    ) : null}
                </div>
                <Input
                    type="text"
                    id="choose-amount"
                    iconLeft={
                        getCountry() === 'UK' ? <IconPound /> : <IconEuro />
                    }
                    min={minAmount}
                    max={maxAmount}
                    className="w-full"
                    value={intl.formatNumber(selectedAmountOrMaxAmount)}
                    onChange={(e) => {
                        e.persist();
                        debounceSetSelectedAmount(e.target.value, e);
                    }}
                />
                <SelectedAmountValidationError
                    selectedAmount={selectedAmountOrMaxAmount}
                    maxAvailableCash={maxAmount}
                />
            </div>

            <div className="w-full">
                <Slider
                    min={minAmount}
                    max={maxAmount}
                    defaultValue={maxAmount}
                    value={Number(selectedAmount)}
                    onChange={(e) => {
                        e.persist();
                        debounceSetSelectedAmount(e.target.value, e);
                    }}
                    step={10}
                />
                <div className="text-sm text-grey-600 mt-l flex w-full justify-between">
                    <span className="text-s text-structure-40">
                        <FormattedCurrency value={minAmount} />
                    </span>
                    <span className="text-s text-structure-40">
                        <FormattedCurrency value={maxAmount} />
                    </span>
                </div>
            </div>
        </div>
    );
};

const SelectedAmountValidationError = ({
    selectedAmount,
    maxAvailableCash
}: {
    selectedAmount: number;
    maxAvailableCash: number;
}) => {
    if (
        selectedAmountGreaterThanMaxAvailableCash(
            selectedAmount,
            maxAvailableCash
        )
    ) {
        return (
            <ErrorMessage>
                <FormattedMessage
                    id="_.apply_flexi_loan.requestedAmount.validation.tooHigh"
                    defaultMessage="Please enter an amount less than {maxAmount, number, :: currency/GBP precision-integer}"
                    values={{
                        maxAmount: maxAvailableCash
                    }}
                />
            </ErrorMessage>
        );
    }

    if (
        selectedAmount === 0 ||
        selectedAmountLessThanMinDrawDown(selectedAmount)
    ) {
        return (
            <ErrorMessage>
                <FormattedMessage
                    id="_.apply_flexi_loan.requestedAmount.validation.tooLow"
                    defaultMessage="Please enter an amount greater than {minAmount, number, :: currency/GBP precision-integer}"
                    values={{
                        minAmount: Constants.MIN_DRAW_DOWN_AMOUNT
                    }}
                />
            </ErrorMessage>
        );
    }

    return null;
};
